import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { securityKeys } from '@/hooks/api/query-keys';

// ============ Types ============

export interface Session {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  lastActive: string;
  isCurrent: boolean;
}

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  requireTeam2FA: boolean;
}

interface TwoFactorSetupResponse {
  qrCodeDataUrl: string;
  secret: string;
}

interface EnableTwoFactorResponse {
  message: string;
}

// ============ Query Hooks ============

export function useSessions() {
  return useQuery({
    queryKey: securityKeys.sessions(),
    queryFn: () => fetchApi<Session[]>('/auth/sessions'),
  });
}

/**
 * Combines /auth/2fa/status and /auth/2fa/company-requirement into a single
 * SecuritySettings shape that the UI expects.
 */
export function useSecuritySettings() {
  return useQuery({
    queryKey: securityKeys.settings(),
    queryFn: async (): Promise<SecuritySettings> => {
      const [status, requirement] = await Promise.all([
        fetchApi<{ enabled: boolean; hasRecoveryCodes: boolean }>('/auth/2fa/status'),
        fetchApi<{ required: boolean; companyName: string }>('/auth/2fa/company-requirement'),
      ]);
      return {
        twoFactorEnabled: status.enabled,
        requireTeam2FA: requirement.required,
      };
    },
  });
}

// ============ Mutation Hooks ============

/**
 * Enable 2FA: calls POST /auth/2fa/setup to initialize, then
 * POST /auth/2fa/enable with the TOTP code to activate.
 *
 * Setup returns the QR code and the secret for the UI to show; the API keeps
 * the secret itself. useEnableTwoFactor below finalizes with the code alone.
 */
export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: () =>
      fetchApi<TwoFactorSetupResponse>('/auth/2fa/setup', { method: 'POST' }),
  });
}

/**
 * The body is exactly `{ code }`. EnableTwoFactorDto declares nothing else and
 * the API's ValidationPipe runs with forbidNonWhitelisted, so the `secret` this
 * hook used to forward came back 400 "property secret should not exist", and
 * nobody could turn on 2FA from Settings (locale audit 2026-09-26, hand-off from
 * lane A17). Only `code` is picked, whatever a caller passes.
 */
export function useEnableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code }: { code: string }) =>
      fetchApi<EnableTwoFactorResponse>('/auth/2fa/enable', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.settings() });
    },
  });
}

export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { password: string }) =>
      fetchApi<{ success: boolean }>('/auth/2fa/disable', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.settings() });
    },
  });
}

/**
 * Combined update hook for company-level settings (requireTeam2FA).
 *
 * NOTE: twoFactorEnabled toggle is intentionally NOT handled here.
 * Enabling 2FA requires a TOTP enrollment ceremony (QR scan + verify).
 * Disabling requires current-password confirmation.
 * Both flows are handled via useSetupTwoFactor + useEnableTwoFactor
 * and useDisableTwoFactor directly from the profile page.
 */
export function useUpdateSecuritySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<SecuritySettings>) => {
      if (data.requireTeam2FA !== undefined) {
        // Update company-level 2FA requirement
        await fetchApi('/auth/2fa/company-requirement', {
          method: 'PUT',
          body: JSON.stringify({ required: data.requireTeam2FA }),
        });
      }
      // twoFactorEnabled: not handled here — use useEnableTwoFactor /
      // useDisableTwoFactor directly (each requires user interaction).
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.settings() });
    },
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ success: boolean }>(`/auth/sessions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.sessions() });
    },
  });
}

/**
 * Revoke all other sessions by fetching the current session list and
 * deleting each non-current session individually.
 * Uses the existing DELETE /auth/sessions/:id endpoint.
 */
export function useRevokeAllOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Fetch current sessions to find non-current ones
      const sessions = await fetchApi<Session[]>('/auth/sessions');
      const otherSessions = sessions.filter((s) => !s.isCurrent);
      // Revoke each non-current session
      await Promise.all(
        otherSessions.map((s) =>
          fetchApi<{ success: boolean }>(`/auth/sessions/${s.id}`, {
            method: 'DELETE',
          }),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.sessions() });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      fetchApi<{ message: string; otherSessionsSignedOut: boolean }>(
        '/auth/change-password',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
      ),
    // Changing a password now ends every OTHER session of this user, so the
    // session list on the same page is stale the moment this resolves. It had no
    // onSuccess at all — unlike useRevokeSession / useRevokeAllOtherSessions
    // right above, which have always invalidated this key.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: securityKeys.sessions() });
    },
  });
}
